import * as React from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';

export type TooltipPlacement = 'top' | 'bottom';

export interface TooltipProps {
  /** Bubble text. */
  label: React.ReactNode;
  placement?: TooltipPlacement;
  disabled?: boolean;
  maxWidth?: number;
  /** The element the tooltip describes. */
  children: React.ReactNode;
  className?: string;
}

const SHOW_DELAY_MS = 300;
const OFFSET_PX = 8;

/**
 * Lumen tooltip — hover/focus label. Wrap the element it describes.
 *
 * The bubble is rendered in a portal on document.body with fixed
 * positioning, so it is never clipped by `overflow` ancestors (scroll
 * areas, cards, modals).
 */
export function Tooltip({
  label,
  placement = 'top',
  disabled = false,
  maxWidth = 320,
  children,
  className
}: TooltipProps) {
  const [anchorRect, setAnchorRect] = React.useState<DOMRect | null>(null);
  const triggerRef = React.useRef<HTMLSpanElement>(null);
  const timerRef = React.useRef<number | undefined>(undefined);

  const hide = React.useCallback(() => {
    window.clearTimeout(timerRef.current);
    setAnchorRect(null);
  }, []);

  const beginShow = React.useCallback(() => {
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      if (triggerRef.current) {
        setAnchorRect(triggerRef.current.getBoundingClientRect());
      }
    }, SHOW_DELAY_MS);
  }, []);

  // Clear the pending show timer on unmount.
  React.useEffect(() => () => window.clearTimeout(timerRef.current), []);

  // The fixed-position bubble does not follow the page while scrolling,
  // so hide it as soon as any scrolling happens.
  React.useEffect(() => {
    if (anchorRect == null) return undefined;
    window.addEventListener('scroll', hide, true);
    return () => window.removeEventListener('scroll', hide, true);
  }, [anchorRect, hide]);

  const interactive = !disabled;

  return (
    <Trigger
      ref={triggerRef}
      className={className}
      onMouseEnter={interactive ? beginShow : undefined}
      onMouseLeave={interactive ? hide : undefined}
      onFocus={interactive ? beginShow : undefined}
      onBlur={interactive ? hide : undefined}
    >
      {children}
      {interactive &&
        anchorRect != null &&
        createPortal(
          <Bubble
            role="tooltip"
            $placement={placement}
            $maxWidth={maxWidth}
            style={{
              left: anchorRect.left + anchorRect.width / 2,
              top:
                placement === 'top'
                  ? anchorRect.top - OFFSET_PX
                  : anchorRect.bottom + OFFSET_PX
            }}
          >
            {label}
          </Bubble>,
          document.body
        )}
    </Trigger>
  );
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Trigger = styled.span`
  display: inline-flex;
`;

const Bubble = styled.span<{
  $placement: TooltipPlacement;
  $maxWidth: number;
}>`
  position: fixed;
  width: max-content;
  max-width: ${({ $maxWidth }) => $maxWidth}px;
  background: ${({ theme }) => theme.color.slate[900]};
  color: #fff;
  padding: 6px 10px;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-family: ${({ theme }) => theme.font.sans};
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 1.5;
  pointer-events: none;
  z-index: 1000;
  box-shadow: ${({ theme }) => theme.shadow.md};
  animation: ${fadeIn} ${({ theme }) => theme.motion.fast}
    ${({ theme }) => theme.motion.ease};

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
  }

  ${({ $placement, theme }) =>
    $placement === 'top'
      ? css`
          transform: translate(-50%, -100%);
          &::after {
            top: 100%;
            border-top-color: ${theme.color.slate[900]};
          }
        `
      : css`
          transform: translateX(-50%);
          &::after {
            bottom: 100%;
            border-bottom-color: ${theme.color.slate[900]};
          }
        `}
`;
